import { modes, errorManager } from '@powerplay/common';
import { TimeManager } from '@powerplay/time-system';
import { playersConfig, COUNTRY_IOC_STRINGS } from './config';
import { PlayersSortTypes, PlayersResultTypes, PlayersSecondResultTypes } from './types';
import { PlayersHelper } from './PlayersHelper';
export class PlayersManager {
    corePhasesManager;
    dnfValue;
    dnsValueDescending;
    dnsValueAscending;
    dsqValue;
    #players = [];
    standings = [];
    dnf = true;
    helper;
    timeManager;
    PLAYABLE_INDEX = 0;
    constructor(corePhasesManager, dnfValue, dnsValueDescending, dnsValueAscending, dsqValue, maxTimeStamp, fpsLimit = 0) {
        this.corePhasesManager = corePhasesManager;
        this.dnfValue = dnfValue;
        this.dnsValueDescending = dnsValueDescending;
        this.dnsValueAscending = dnsValueAscending;
        this.dsqValue = dsqValue;
        this.helper = new PlayersHelper(maxTimeStamp, this.dnfValue);
        this.timeManager = new TimeManager(fpsLimit);
    }
    get players() {
        return this.#players;
    }
    set players(data) {
        this.#players = data;
        const emptyResults = this.getEmptyResults();
        this.#players.forEach((value, key) => {
            const { country } = value;
            this.#players[key].countryString = PlayersManager.getIocCountryString(country);
            this.#players[key].position = key + 1;
            this.#players[key].playable = key === this.PLAYABLE_INDEX;
            let result = value.resultsArr;
            if (result === undefined || result.length === 0) {
                result = emptyResults.notPlayable;
                if (value.playable)
                    result = emptyResults.playable;
            }
            if (value.resultsArr !== undefined && value.resultsArr.length > 0) {
                this.#players[key].resultsArrOriginal = [...value.resultsArr];
                if ((modes.isDailyLeague() || modes.isTournament()) && value.playable)
                    result = emptyResults.playable;
            }
            this.#players[key].resultsArr = [...result];
        });
    }
    setCompareSkillBeforeTimestamp() {
        this.helper.compareSkillBeforeTimestamp = true;
    }
    setStandings(timeRoundCount = 2, provisionalTournamentResults = false, playerOriginalResults = false, miniTableMeters = false) {
        let resultsCopy;
        const originalDataCopyEnabled = playerOriginalResults && this.players[this.PLAYABLE_INDEX].resultsArrOriginal;
        if (originalDataCopyEnabled) {
            resultsCopy = JSON.parse(JSON.stringify(this.players[this.PLAYABLE_INDEX].resultsArr));
            this.players[this.PLAYABLE_INDEX].resultsArr = this.players[this.PLAYABLE_INDEX].resultsArrOriginal;
        }
        this.standings = this.getSortedDataForTable(timeRoundCount, !modes.isDailyLeague(), false, provisionalTournamentResults, miniTableMeters);
        if (originalDataCopyEnabled) {
            this.players[this.PLAYABLE_INDEX].resultsArr = resultsCopy;
        }
    }
    getStandings() {
        return this.standings;
    }
    getEmptyResults() {
        const emptyResultsOpponents = [];
        const emptyResultsPlayer = [];
        let mainPointsPlayer = this.dnsValueDescending;
        let mainPointsOpponent = this.dnsValueDescending;
        if (playersConfig.sortType === PlayersSortTypes.ascending) {
            mainPointsPlayer = this.dnsValueAscending;
            mainPointsOpponent = this.dnsValueAscending;
        }
        let secondPoints;
        if (playersConfig.secondResultType === PlayersSecondResultTypes.number)
            secondPoints = 0;
        if (playersConfig.secondResultType === PlayersSecondResultTypes.numberArray) {
            secondPoints = [0];
        }
        let thirdPoints;
        if (playersConfig.thirdResultType === PlayersSecondResultTypes.number)
            thirdPoints = 0;
        for (let i = 0; i < this.corePhasesManager.disciplineAttemptsCount; i++) {
            const playerObject = {
                main: mainPointsPlayer
            };
            const opponentObject = {
                main: mainPointsOpponent
            };
            if (secondPoints !== undefined) {
                playerObject.second = secondPoints;
                opponentObject.second = secondPoints;
            }
            if (thirdPoints !== undefined) {
                playerObject.third = thirdPoints;
                opponentObject.third = thirdPoints;
            }
            emptyResultsPlayer.push(playerObject);
            emptyResultsOpponents.push(opponentObject);
        }
        return {
            playable: emptyResultsPlayer,
            notPlayable: emptyResultsOpponents
        };
    }
    getPlayer() {
        return this.#players[0];
    }
    getSimilarPositionPlayersForTable(timeRoundCount = 2) {
        const playerPosition = this.getPlayerActualPosition();
        const orderedPlayers = this.getSortedDataForTable(timeRoundCount, !modes.isDailyLeague(), false)
            .filter((playerInfoForTable) => {
            return !['DNF', 'DQ', ''].includes(playerInfoForTable.result ?? 'DNF');
        });
        const players = [];
        if (!playerPosition) {
            throw new Error(errorManager.showBox('player position undefined'));
        }
        if (playerPosition < 3) {
            for (let i = 0; i < 5 && i < orderedPlayers.length; i++) {
                players.push(orderedPlayers[i]);
            }
        }
        else if (playerPosition === orderedPlayers.length) {
            players.push(orderedPlayers[0]);
            for (let i = orderedPlayers.length - 4; i < orderedPlayers.length; i++) {
                players.push(orderedPlayers[i]);
            }
        }
        else {
            players.push(orderedPlayers[0]);
            for (let i = playerPosition - 2; i < playerPosition + 1 && i < orderedPlayers.length; i++) {
                players.push(orderedPlayers[i]);
            }
        }
        return players;
    }
    getPlayerActualPosition(uuid = this.getPlayer().uuid) {
        const isPlayer = uuid === this.getPlayer().uuid;
        const playerData = this.standings.filter((value) => value.uuid === uuid && (value.playable || !isPlayer));
        console.log(playerData);
        return playerData[0]?.position || 0;
    }
    getPlayerMainResult(attempt = this.corePhasesManager.disciplineActualAttempt - 1) {
        return this.getMainResult(this.players[this.PLAYABLE_INDEX], attempt);
    }
    getMainResult(player, attempt, defaultValue = 0) {
        return player.resultsArr?.[attempt]?.main || defaultValue;
    }
    getDataForTable(players = this.players) {
        const retval = [];
        players.forEach((value, index) => {
            retval[index] = {
                position: value.position ?? 0,
                uuid: value.uuid,
                country: value.country ?? '',
                countryString: value.countryString ?? '',
                name: value.name ?? '',
                playable: value.playable ?? false,
                result: '',
                results: [],
                rankingPoints: value.rankingPoints ?? 0,
                attribute: value.attribute.total ?? 0,
                badges: value.badges ?? {}
            };
        });
        return retval;
    }
    getSortedDataForTable(timeRoundCount = 2, removeDuplicatePlayer = true, showBetterResultAsActual = true, provisionalTournamentResults = false, miniTableMeters = false) {
        const retval = [];
        let playerIndex = -1;
        let duplicatePlayerIndex = -1;
        const undefinedValue = playersConfig.sortType === PlayersSortTypes.ascending ?
            this.dnfValue :
            0;
        const players = [];
        players.push(...this.players.map((value, index) => {
            const playerInfo = JSON.parse(JSON.stringify(value));
            playerInfo.resultsArr = playerInfo.resultsArr?.filter((_val, key) => {
                return provisionalTournamentResults || key < this.corePhasesManager.disciplineActualAttempt;
            });
            playerInfo.finalResult = playerInfo.resultsArr?.reduce((acc, val) => (acc += val.main), 0);
            if (playersConfig.resultType === PlayersResultTypes.meters) {
                console.log(playerInfo.name, playerInfo.finalResult, playerInfo);
                playerInfo.resultsArrOrdered = JSON.parse(JSON.stringify(playerInfo.resultsArr)).sort((a, b) => {
                    return b.main - a.main;
                });
                playerInfo.finalResult = playerInfo.resultsArrOrdered[0].main;
            }
            playerInfo.finalResultSecond = playerInfo.resultsArr?.reduce((acc, val) => (acc += (typeof val?.second === 'number' ? val?.second : undefinedValue)), 0);
            playerInfo.finalResultThird = playerInfo.resultsArr?.reduce((acc, val) => (acc += (typeof val?.third === 'number' ? val?.third : undefinedValue)), 0);
            if (value.playable)
                playerIndex = index;
            return playerInfo;
        }));
        if (this.players[this.PLAYABLE_INDEX].resultsArrOriginal) {
            const playerInfo = JSON.parse(JSON.stringify(this.players[this.PLAYABLE_INDEX]));
            playerInfo.resultsArr = playerInfo.resultsArrOriginal;
            playerInfo.resultsArr = playerInfo.resultsArr?.filter((_val, key) => {
                return key < this.corePhasesManager.disciplineActualAttempt;
            });
            playerInfo.finalResult = playerInfo.resultsArr?.reduce((acc, val) => (acc += val.main), 0);
            playerInfo.finalResultSecond = playerInfo.resultsArr?.reduce((acc, val) => (acc += (typeof val?.second === 'number' ? val?.second : 0)), 0);
            playerInfo.playable = false;
            duplicatePlayerIndex = players.push(playerInfo) - 1;
        }
        const actualPlayer = players[playerIndex];
        const duplicatePlayer = players[duplicatePlayerIndex] || [{}];
        if (actualPlayer.finalResult !== undefined &&
            duplicatePlayer.finalResult !== undefined) {
            if (showBetterResultAsActual) {
                let actualResult = actualPlayer.finalResult;
                let serverResult = duplicatePlayer.finalResult;
                if (actualResult === serverResult) {
                    actualResult = actualPlayer.finalResultSecond ?? undefinedValue;
                    serverResult = duplicatePlayer.finalResultSecond ?? undefinedValue;
                }
                if (playersConfig.sortType === PlayersSortTypes.ascending) {
                    players[playerIndex].playable = (actualResult < serverResult);
                    players[duplicatePlayerIndex].playable = !players[playerIndex].playable;
                }
                else {
                    players[playerIndex].playable = (actualResult > serverResult);
                    players[duplicatePlayerIndex].playable = !players[playerIndex].playable;
                }
            }
            if (removeDuplicatePlayer) {
                if (!players[playerIndex].playable) {
                    players.splice(playerIndex, 1);
                    duplicatePlayerIndex--;
                    playerIndex = -1;
                }
                if (!players[duplicatePlayerIndex].playable) {
                    players.splice(duplicatePlayerIndex, 1);
                    duplicatePlayerIndex = -1;
                }
            }
        }
        players.sort(playersConfig.sortType === PlayersSortTypes.ascending ?
            this.helper.sortingFunctionAscending :
            this.helper.sortingFunctionDescending);
        const bestResult = players[0].finalResult || 0;
        players.forEach((value, index) => {
            const results = value.resultsArr?.map((val) => {
                const valCopy = {
                    main: val.main?.toString(),
                    second: val.second?.toString(),
                    third: val.third?.toString()
                };
                if (playersConfig.resultType === PlayersResultTypes.metersPoints) {
                    const second = val.second;
                    valCopy.main = `${val.main.toFixed(1)}p`;
                    valCopy.second = `${second.toFixed(1)}m`;
                }
                return valCopy;
            }) ?? [];
            const result = value.finalResult;
            let resultMain = '';
            if (result === undefined || result === this.dnfValue) {
                resultMain = 'DNF';
                results[0].second = '';
            }
            else if (result === this.dsqValue) {
                resultMain = 'DQ';
                results[0].second = '';
            }
            else if (result === this.dnsValueAscending || result < 0) {
                resultMain = '';
                results.forEach((resultItem) => {
                    resultItem.second = '';
                });
            }
            else if (playersConfig.resultType === PlayersResultTypes.time) {
                resultMain = this.timeManager.getTimeInFormatFromSeconds(result, timeRoundCount, playersConfig.roundingStandings);
                if (playersConfig.addTimes && index > 0) {
                    resultMain = `+${this.timeManager.getTimeInFormatFromSeconds(result - bestResult, timeRoundCount, playersConfig.roundingStandings)}`;
                }
            }
            else if (playersConfig.resultType === PlayersResultTypes.metersPoints) {
                resultMain = `${result.toFixed(1)}p`;
            }
            else if (playersConfig.resultType === PlayersResultTypes.points) {
                resultMain = result.toFixed();
            }
            else if (playersConfig.resultType === PlayersResultTypes.meters) {
                resultMain = result.toFixed(timeRoundCount);
                if (result === 0)
                    resultMain = miniTableMeters ? 'X' : 'NM';
            }
            retval[index] = {
                position: index + 1,
                uuid: value.uuid,
                country: value.country ?? '',
                countryString: value.countryString ?? '',
                name: value.name ?? '',
                playable: value.playable ?? false,
                result: resultMain,
                results: results.map((val) => {
                    if (val.third !== undefined) {
                        return `${val.second},${val.third}`;
                    }
                    return val.second || val.third || '';
                }),
                resultsFull: results,
                badges: value.badges ?? {}
            };
        });
        return retval;
    }
    setPlayerResultsDNF() {
        if (playersConfig.sortType === PlayersSortTypes.ascending) {
            if (this.players[this.PLAYABLE_INDEX].resultsArr?.[0].main === this.dnsValueAscending) {
                this.setPlayerResults(this.dnfValue);
                this.dnf = true;
            }
        }
        else {
            this.players[this.PLAYABLE_INDEX].resultsArr?.forEach((result) => {
                if (result.main === -1)
                    result.main = 0;
            });
        }
    }
    setPlayerResults(resultMain, resultSecond, resultThird) {
        this.dnf = false;
        const attemptIndex = this.corePhasesManager.disciplineActualAttempt - 1;
        if (!this.players[this.PLAYABLE_INDEX].resultsArr) {
            this.players[this.PLAYABLE_INDEX].resultsArr = [];
        }
        this.players[this.PLAYABLE_INDEX].resultsArr[attemptIndex] = {
            main: resultMain,
            second: resultSecond,
            third: resultThird
        };
        if (!this.players[this.PLAYABLE_INDEX].resultTimestamp) {
            this.players[this.PLAYABLE_INDEX].resultTimestamp = Date.now();
        }
        const { resultTimestampBest } = this.players[this.PLAYABLE_INDEX];
        if (!this.isPlayerImproved() && resultTimestampBest) {
            this.players[this.PLAYABLE_INDEX].resultTimestamp = resultTimestampBest;
        }
    }
    editPlayerResultsById(uuid, attemptIndex = this.corePhasesManager.disciplineActualAttempt - 1, resultMain, resultSecond, resultThird, withSetTimestamp = true) {
        const player = this.getPlayerById(uuid);
        if (player === undefined)
            return;
        if (!player.resultsArr)
            player.resultsArr = [];
        player.resultsArr[attemptIndex] = {
            main: resultMain !== undefined ? resultMain : player.resultsArr[attemptIndex].main,
            second: resultSecond !== undefined ? resultSecond : player.resultsArr[attemptIndex].second,
            third: resultThird !== undefined ? resultThird : player.resultsArr[attemptIndex].third
        };
        if (!player.resultTimestamp && withSetTimestamp) {
            player.resultTimestamp = Date.now();
        }
        if (!this.isPlayerImproved() && player.resultTimestampBest) {
            player.resultTimestamp = player.resultTimestampBest;
        }
    }
    setPlayerResultsById(uuid, resultMain, resultSecond, withSetTimestamp = true) {
        const player = this.getPlayerById(uuid);
        if (player === undefined)
            return;
        const attemptIndex = this.corePhasesManager.disciplineActualAttempt - 1;
        if (!player.resultsArr)
            player.resultsArr = [];
        player.resultsArr[attemptIndex] = {
            main: resultMain,
            second: resultSecond
        };
        if (!player.resultTimestamp && withSetTimestamp) {
            player.resultTimestamp = Date.now();
        }
        if (!this.isPlayerImproved() && player.resultTimestampBest) {
            player.resultTimestamp = player.resultTimestampBest;
        }
    }
    isPlayerImproved() {
        const ascending = playersConfig.sortType === PlayersSortTypes.ascending;
        const resultsArr = this.players[this.PLAYABLE_INDEX].resultsArr?.[0];
        const resultsArrOriginal = this.players[this.PLAYABLE_INDEX].resultsArrOriginal?.[0];
        let finalResult = resultsArr?.main;
        let finalOriginal = resultsArrOriginal?.main;
        const undefinedValue = ascending ? this.dnfValue : 0;
        if (finalResult === finalOriginal) {
            finalResult = typeof resultsArr?.second === 'number' ?
                resultsArr.second :
                undefinedValue;
            finalOriginal = typeof resultsArrOriginal?.second === 'number' ?
                resultsArrOriginal.second :
                undefinedValue;
        }
        return (!finalOriginal ||
            !finalResult ||
            (ascending && finalResult < finalOriginal) ||
            (!ascending && finalResult > finalOriginal));
    }
    isPlayerSplitTimeImproved(index, timeToCompare) {
        const ascending = playersConfig.sortType === PlayersSortTypes.ascending;
        const undefinedValue = ascending ? this.dnfValue : 0;
        const splitOriginalTime = this.getPlayer()?.split?.[index] ?? undefinedValue;
        return (!splitOriginalTime ||
            !timeToCompare ||
            (ascending && timeToCompare < splitOriginalTime) ||
            (!ascending && timeToCompare > splitOriginalTime));
    }
    getBestResult(sortType = PlayersSortTypes.ascending, excludeIds = [], onlyPlayer = false) {
        const defaultValue = sortType === PlayersSortTypes.ascending ? this.dnfValue : 0;
        const finalResults = [];
        this.players.filter(value => {
            return (!onlyPlayer && !excludeIds.includes(value.uuid)) || (onlyPlayer && value.playable);
        }).forEach((value) => {
            if (playersConfig.resultType === PlayersResultTypes.meters) {
                finalResults.push(Math.max.apply(Math, value.resultsArr?.map((val, key) => (key < this.corePhasesManager.disciplineActualAttempt) ?
                    val.main :
                    0) ?? []));
            }
            else {
                finalResults
                    .push(value.resultsArr?.filter((_val, key) => (key < this.corePhasesManager.disciplineActualAttempt))
                    .reduce((acc, val) => (acc += val.main), 0) || defaultValue);
            }
        });
        if (sortType === PlayersSortTypes.ascending)
            return Math.min.apply(Math, finalResults);
        return Math.max.apply(Math, finalResults);
    }
    getBestResultPlayerInfo(sortType = PlayersSortTypes.ascending, excludePlayable = false) {
        const defaultValue = sortType === PlayersSortTypes.ascending ? this.dnfValue : 0;
        let bestPlayerIndex = 0;
        let bestResultTemp = defaultValue;
        const players = [...this.players];
        if (players[this.PLAYABLE_INDEX].resultsArrOriginal) {
            const playerCopy = JSON.parse(JSON.stringify(players[this.PLAYABLE_INDEX]));
            playerCopy.resultsArr = playerCopy.resultsArrOriginal;
            playerCopy.resultsArr = playerCopy.resultsArr?.filter((_val, key) => {
                return key < this.corePhasesManager.disciplineActualAttempt;
            });
            playerCopy.finalResult = playerCopy.resultsArr?.reduce((acc, val) => (acc += val.main), 0);
            playerCopy.playable = false;
            players.push(playerCopy);
        }
        players.forEach((value, index) => {
            if (excludePlayable && value.playable)
                return;
            const dataToFilter = value.resultsArr || [];
            const result = dataToFilter
                .filter((_val, key) => (key < this.corePhasesManager.disciplineActualAttempt))
                .reduce((acc, val) => (acc += val.main), 0) || defaultValue;
            if ((sortType === PlayersSortTypes.ascending && result < bestResultTemp) ||
                (sortType === PlayersSortTypes.descending && result > bestResultTemp)) {
                bestResultTemp = result;
                bestPlayerIndex = index;
            }
        });
        const bestPlayer = JSON.parse(JSON.stringify(players[bestPlayerIndex]));
        bestPlayer.finalResult = bestResultTemp;
        return bestPlayer;
    }
    getBestSecondary(sortType = PlayersSortTypes.ascending) {
        const defaultValue = sortType === PlayersSortTypes.ascending ? this.dnfValue : 0;
        const finalResults = [];
        this.players.forEach((value) => {
            finalResults
                .push(value.resultsArr?.filter((_val, key) => (key < this.corePhasesManager.disciplineActualAttempt))
                .reduce((acc, val) => {
                if (val.second instanceof Array) {
                    throw new Error(errorManager.showBox('get best secondary from array not implemented'));
                }
                return (acc += val.second || 0);
            }, 0) || defaultValue);
        });
        if (sortType === PlayersSortTypes.ascending)
            return Math.min.apply(Math, finalResults);
        return Math.max.apply(Math, finalResults);
    }
    getPlayersPositions() {
        const uuidPlayer = this.getPlayer().uuid;
        let firstPlayerDone = false;
        return this.standings.filter((value) => {
            if (value.uuid === uuidPlayer) {
                if (firstPlayerDone)
                    return false;
                firstPlayerDone = true;
            }
            return true;
        }).map((value) => value.uuid);
    }
    static getIocCountryString(country) {
        return (COUNTRY_IOC_STRINGS[country.toUpperCase()] || country).toLowerCase();
    }
    getBestAttribute() {
        const powers = this.#players.map(obj => obj.attribute.total);
        return Math.max(...powers);
    }
    getSplitTimesOfPlayers(splitTimeIndex, isFinish = false, usePlayerOriginalResults = false) {
        const players = this.#players.map((player) => {
            const playerInfo = JSON.parse(JSON.stringify(player));
            if (isFinish) {
                if (usePlayerOriginalResults) {
                    playerInfo.finalResult = player.resultsArrOriginal?.[0].main || this.dnfValue;
                    return playerInfo;
                }
                playerInfo.finalResult = player.resultsArr?.[0].main || this.dnfValue;
                return playerInfo;
            }
            if (playerInfo.split?.[splitTimeIndex]) {
                playerInfo.finalResult = playerInfo.split?.[splitTimeIndex];
                return playerInfo;
            }
            playerInfo.finalResult = this.dnfValue;
            return playerInfo;
        }).sort(this.helper.sortingFunctionAscending);
        return players;
    }
    getPlayerPositionBySplitTime(splitTime, splitTimeIndex, isFinish = false, excludeIds = []) {
        const players = this.getSplitTimesOfPlayers(splitTimeIndex, isFinish).filter((player) => {
            return !excludeIds.includes(player.uuid);
        });
        let position = 1;
        players.forEach((player, index) => {
            if (player.finalResult === undefined || player.finalResult > splitTime)
                return position;
            position = index + 2;
        });
        return position;
    }
    getPlayersSortedByAttribute(ascending = false) {
        let players = JSON.parse(JSON.stringify(this.#players));
        if (ascending) {
            players = players.reverse();
        }
        return players.sort(this.helper.sortingFunctionAttribute);
    }
    getPlayerById(uuid) {
        return this.#players.find((p) => {
            return p.uuid === uuid;
        });
    }
    setPlayerById(uuid, player) {
        const playerIndex = this.#players.findIndex((p) => {
            return p.uuid === uuid;
        });
        this.#players[playerIndex] = player;
    }
    getOrderedPlayersPositionByRankingPoints() {
        const players = [];
        players.push(...this.players);
        players.sort((a, b) => {
            if (a.rankingPoints === undefined || b.rankingPoints === undefined)
                return -1;
            return b.rankingPoints - a.rankingPoints;
        });
        players.forEach((player, index) => {
            player.position = index + 1;
        });
        return players;
    }
    getOrderedPlayersPositionByAttribute() {
        const players = [];
        players.push(...this.players);
        players.sort((a, b) => {
            if (a.attribute.total === undefined || b.attribute.total === undefined)
                return -1;
            return b.attribute.total - a.attribute.total;
        });
        players.forEach((player, index) => {
            player.position = index + 1;
        });
        return players;
    }
    getCountedResultsMainSoFar(uuid, attempt = this.corePhasesManager.disciplineActualAttempt) {
        const player = this.getPlayerById(uuid);
        if (!player)
            return 0;
        const resultsArr = player.resultsArr ?? [];
        const countedResultsMain = resultsArr
            .filter((_val, key) => (key < attempt))
            .reduce((acc, val) => (acc += val.main), 0);
        return countedResultsMain;
    }
    reset() {
        this.dnf = true;
        this.standings = [];
    }
}
//# sourceMappingURL=PlayersManager.js.map